import { useEffect, useState, useCallback } from "react";
import {
  Container,
  Typography,
  Paper,
  Table,
  TableHead,
  TableRow,
  CircularProgress,
  TableCell,
  TableBody,
  Chip,
  Button,
  Box,
  Dialog,
  Alert,
  DialogTitle,
  DialogContent,
  DialogActions,
  TablePagination,
  InputAdornment,
  TextField,
  MenuItem,
} from "@mui/material";

import { supabase } from "../lib/supabaseClient";
import SearchIcon from "@mui/icons-material/Search";

const SEARCH_DEBOUNCE_MS = 350;

export default function AuditLogs() {
  const [loading, setLoading] = useState(false);

  // Audit Data
  const [logs, setLogs] = useState([]);
  const [totalCount, setTotalCount] = useState(0);

  // Pagination State
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  // Filter/Search State
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 7); // Default to last 7 days for logs
    return d.toISOString().split("T")[0];
  });
  const [endDate, setEndDate] = useState(
    new Date().toISOString().split("T")[0],
  );

  const [selectedTable, setSelectedTable] = useState("all");
  const [selectedAction, setSelectedAction] = useState("all");
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  // Details Modal State
  const [selectedLog, setSelectedLog] = useState(null);

  // Debounce the search box so we're not round-tripping to the RPC on
  // every keystroke - commit to the actual query term after a short pause.
  useEffect(() => {
    const timeout = setTimeout(() => {
      setSearchQuery(searchInput);
      setPage(0);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timeout);
  }, [searchInput]);

  const fetchLogs = useCallback(async () => {
    setLoading(true);

    const startTimestamp = `${startDate}T00:00:00.000Z`;
    const endTimestamp = `${endDate}T23:59:59.999Z`;

    // All filtering (date range, table, action, search) and pagination
    // happen inside this RPC, not in the browser - see
    // supabase/migrations/20260722130000_audit_log_search_rpc.sql. That
    // avoids the old fetch-everything-then-filter-in-JS approach, which
    // silently dropped rows once a query exceeded PostgREST's row cap.
    const { data, error } = await supabase.rpc("search_audit_logs", {
      p_start: startTimestamp,
      p_end: endTimestamp,
      p_table_name: selectedTable === "all" ? null : selectedTable,
      p_action: selectedAction === "all" ? null : selectedAction,
      p_search: searchQuery || null,
      p_page: page,
      p_page_size: rowsPerPage,
    });

    if (error) {
      console.error("Database Error:", error.message);
      setLogs([]);
      setTotalCount(0);
    } else {
      setLogs(data || []);
      setTotalCount(data?.[0]?.total_count ?? 0);
    }
    setLoading(false);
  }, [
    startDate,
    endDate,
    selectedTable,
    selectedAction,
    searchQuery,
    page,
    rowsPerPage,
  ]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  // ------------- Handlers ----------------- //

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    window.scrollTo({ top: 0, behavior: "instant" });
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (e) => {
    setSearchInput(e.target.value);
  };

  const handleResetFilters = () => {
    setSearchInput("");
    setSearchQuery("");
    setSelectedTable("all");
    setSelectedAction("all");
    const d = new Date();
    d.setDate(d.getDate() - 7);
    setStartDate(d.toISOString().split("T")[0]);
    setEndDate(new Date().toISOString().split("T")[0]);
    setPage(0);
  };

  // Helper to get action chip colors
  const getActionColor = (action) => {
    switch (action) {
      case "INSERT":
        return "success";
      case "UPDATE":
        return "warning";
      case "DELETE":
        return "error";
      default:
        return "default";
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box mb={3}>
        <Typography
          variant="h4"
          color="primary.main"
          sx={{ fontWeight: "bold" }}
        >
          System Audit Logs
        </Typography>
      </Box>

      {/* FILTER LEVEL */}
      <Paper
        variant="outlined"
        sx={{ p: 2, mb: 4, bgcolor: "grey.50", borderRadius: 2 }}
      >
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          {/* Left Side: Dates and Dropdowns */}
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 2,
              alignItems: "center",
              flex: "1 1 auto",
            }}
          >
            <TextField
              label="From Date"
              type="date"
              size="small"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              sx={{
                bgcolor: "white",
                flex: { xs: "1 1 100%", sm: "1 1 auto" },
                minWidth: 140,
              }}
              slotProps={{ inputLabel: { shrink: true } }}
            />
            <TextField
              label="To Date"
              type="date"
              size="small"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              sx={{
                bgcolor: "white",
                flex: { xs: "1 1 100%", sm: "1 1 auto" },
                minWidth: 140,
              }}
              slotProps={{ inputLabel: { shrink: true } }}
            />

            <TextField
              select
              label="Action"
              size="small"
              value={selectedAction}
              onChange={(e) => {
                setSelectedAction(e.target.value);
                setPage(0);
              }}
              sx={{
                bgcolor: "white",
                flex: { xs: "1 1 100%", sm: "1 1 auto" },
                minWidth: 140,
              }}
            >
              <MenuItem value="all">All Actions</MenuItem>
              <MenuItem value="INSERT">Insert</MenuItem>
              <MenuItem value="UPDATE">Update</MenuItem>
              <MenuItem value="DELETE">Delete</MenuItem>
            </TextField>

            <TextField
              select
              label="Table"
              size="small"
              value={selectedTable}
              onChange={(e) => {
                setSelectedTable(e.target.value);
                setPage(0);
              }}
              sx={{
                bgcolor: "white",
                flex: { xs: "1 1 100%", sm: "1 1 auto" },
                minWidth: 140,
              }}
            >
              <MenuItem value="all">All Tables</MenuItem>
              <MenuItem value="lesson_logs">Sessions</MenuItem>
              <MenuItem value="students">Students</MenuItem>
              <MenuItem value="tutors">Tutors</MenuItem>
              <MenuItem value="profiles">Profiles</MenuItem>
              <MenuItem value="student_tutor_assignments">Assignments</MenuItem>
            </TextField>
          </Box>

          {/* Right Side: Search Bar */}
          <TextField
            size="small"
            placeholder="Search payload or user ID..."
            value={searchInput}
            onChange={handleSearchChange}
            sx={{
              bgcolor: "white",
              flex: { xs: "1 1 100%", md: "0 0 350px" },
            }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              },
            }}
          />
        </Box>
      </Paper>

      {/* TABLE */}
      <Paper
        sx={{
          width: "100%",
          overflow: "hidden",
          minHeight: 600,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flexGrow: 1,
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <>
            {logs.length > 0 ? (
              <Box sx={{ width: "100%", overflowX: "auto", flexGrow: 1 }}>
                <Table sx={{ tableLayout: "fixed", minWidth: 1200 }}>
                  <TableHead sx={{ bgcolor: "grey.100" }}>
                    <TableRow>
                      <TableCell sx={{ width: 170 }}>Timestamp</TableCell>
                      <TableCell sx={{ width: 110 }}>Action</TableCell>
                      <TableCell sx={{ width: 150 }}>Author</TableCell>
                      <TableCell sx={{ width: 150 }}>Tutor</TableCell>
                      <TableCell sx={{ width: 150 }}>Student</TableCell>
                      <TableCell sx={{ width: 160 }}>Table</TableCell>
                      <TableCell sx={{ width: 180 }}>Changes Made</TableCell>
                      <TableCell align="right" sx={{ width: 100 }}>
                        Details
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {logs.map((log) => (
                      <TableRow key={log.id} hover sx={{ height: 64 }}>
                        {/* 1. Timestamp */}
                        <TableCell>
                          <Typography variant="body2">
                            {new Date(log.action_timestamp).toLocaleString()}
                          </Typography>
                        </TableCell>

                        {/* 2. Action */}
                        <TableCell>
                          <Chip
                            label={log.action}
                            size="small"
                            color={getActionColor(log.action)}
                            variant="outlined"
                            sx={{ fontWeight: "bold" }}
                          />
                        </TableCell>

                        {/* 3. Author */}
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {log.author_name
                              ? log.author_name
                              : log.auth_user_id
                                ? "Unknown User"
                                : "System"}
                          </Typography>
                          {log.auth_user_id && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: "block", fontSize: "0.65rem" }}
                            >
                              {log.auth_user_id.split("-")[0]}...
                            </Typography>
                          )}
                        </TableCell>

                        {/* 4. Tutor */}
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {log.tutor_name || "N/A"}
                          </Typography>
                        </TableCell>

                        {/* 5. Student */}
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {log.student_name || "N/A"}
                          </Typography>
                        </TableCell>

                        {/* 6. Table */}
                        <TableCell>
                          <Typography
                            variant="body2"
                            fontWeight="medium"
                            sx={{ overflowWrap: "anywhere" }}
                          >
                            {log.table_name}
                          </Typography>
                        </TableCell>

                        {/* 7. Changes Made */}
                        <TableCell>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: "block", overflowWrap: "anywhere" }}
                          >
                            {log.changed_fields
                              ? Object.keys(log.changed_fields)
                                  .filter((key) => key !== "updated_at")
                                  .join(", ")
                              : "N/A"}
                          </Typography>
                        </TableCell>

                        {/* 8. Details */}
                        <TableCell align="right">
                          <Button
                            size="small"
                            variant="text"
                            onClick={() => setSelectedLog(log)}
                          >
                            View JSON
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            ) : (
              <Box sx={{ p: 2, flexGrow: 1 }}>
                <Alert
                  severity="info"
                  variant="outlined"
                  action={
                    <Button
                      color="primary"
                      size="small"
                      onClick={handleResetFilters}
                    >
                      Clear Filters
                    </Button>
                  }
                >
                  <Typography variant="body2" fontWeight="bold">
                    No audit logs found.
                  </Typography>
                  <Typography variant="caption">
                    Try widening your date range or clearing your search query.
                  </Typography>
                </Alert>
              </Box>
            )}

            <TablePagination
              rowsPerPageOptions={[20, 50, 100]}
              component="div"
              count={totalCount}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </>
        )}
      </Paper>

      {/* JSON DETAILS MODAL */}
      <Dialog
        open={Boolean(selectedLog)}
        onClose={() => setSelectedLog(null)}
        fullWidth
        maxWidth="sm"
      >
        {selectedLog && (
          <>
            <DialogTitle sx={{ fontWeight: "bold", pb: 1 }}>
              Record Details: {selectedLog.table_name}
            </DialogTitle>
            <DialogContent dividers>
              <Box mb={2} display="flex" gap={2}>
                <Chip
                  label={selectedLog.action}
                  size="small"
                  color={getActionColor(selectedLog.action)}
                />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ alignSelf: "center" }}
                >
                  {new Date(selectedLog.action_timestamp).toLocaleString()}
                </Typography>
              </Box>

              {selectedLog.action === "UPDATE" &&
                selectedLog.changed_fields && (
                  <Box mb={3}>
                    <Typography
                      variant="subtitle2"
                      color="primary"
                      gutterBottom
                    >
                      Changed Fields (Delta)
                    </Typography>
                    <Paper
                      sx={{ p: 1.5, bgcolor: "#f8f9fa", overflowX: "auto" }}
                    >
                      <pre style={{ margin: 0, fontSize: "0.85rem" }}>
                        {JSON.stringify(selectedLog.changed_fields, null, 2)}
                      </pre>
                    </Paper>
                  </Box>
                )}

              {(selectedLog.action === "INSERT" ||
                selectedLog.action === "UPDATE") && (
                <Box mb={3}>
                  <Typography variant="subtitle2" gutterBottom>
                    {selectedLog.action === "UPDATE"
                      ? "New State"
                      : "Inserted Data"}
                  </Typography>
                  <Paper sx={{ p: 1.5, bgcolor: "#f8f9fa", overflowX: "auto" }}>
                    <pre style={{ margin: 0, fontSize: "0.85rem" }}>
                      {JSON.stringify(selectedLog.new_data, null, 2)}
                    </pre>
                  </Paper>
                </Box>
              )}

              {(selectedLog.action === "DELETE" ||
                selectedLog.action === "UPDATE") && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    {selectedLog.action === "UPDATE"
                      ? "Previous State"
                      : "Deleted Data"}
                  </Typography>
                  <Paper sx={{ p: 1.5, bgcolor: "#fff3f3", overflowX: "auto" }}>
                    <pre style={{ margin: 0, fontSize: "0.85rem" }}>
                      {JSON.stringify(selectedLog.old_data, null, 2)}
                    </pre>
                  </Paper>
                </Box>
              )}
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
              <Button
                onClick={() => setSelectedLog(null)}
                variant="contained"
                disableElevation
              >
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Container>
  );
}
